import { ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  tradingAPI,
  TradingPortalData,
  TradingSearchResponse,
  TradingArticleSummary,
} from '../../services/api';
import {
  Activity,
  ChevronRight,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import BookmarkButton from '../../components/BookmarkButton';
import BookmarkBatchProvider from '../../components/BookmarkBatchProvider';
import Pagination from '../../components/Pagination';

function articleUrl(article: TradingArticleSummary): string {
  return tradingAPI.getArticleHtmlUrl(article.id);
}

const STUDY_TYPE_LABELS: Record<string, string> = {
  high_52w: '52W High',
  low_52w: '52W Low',
  sma_reclaim: 'SMA Reclaim',
  drawdown_20: '20% Drawdown',
  correction_10: '10% Correction',
  golden_cross: 'Golden Cross',
  death_cross: 'Death Cross',
  macd_bull_cross: 'MACD Bull',
  macd_bear_cross: 'MACD Bear',
  rsi_overbought: 'RSI Overbought',
  rsi_oversold: 'RSI Oversold',
  momentum_surge: 'Mom. Surge',
  momentum_crash: 'Mom. Crash',
  volume_spike: 'Volume Spike',
  price_gap_up: 'Gap Up',
  price_gap_down: 'Gap Down',
  volatility_spike: 'Vol Spike',
  atr_breakout: 'ATR Breakout',
  target_discount: 'Target Discount',
  target_overshoot: 'Target Overshoot',
  win_streak: 'Win Streak',
};

function studyTypeLabel(studyType: string): string {
  return STUDY_TYPE_LABELS[studyType] || studyType.replace(/_/g, ' ');
}

const RATING_BADGE_COLORS: Record<string, string> = {
  strong_bullish: 'bg-emerald-100 text-emerald-700',
  bullish: 'bg-green-50 text-green-600',
  neutral: 'bg-gray-100 text-gray-600',
  bearish: 'bg-orange-50 text-orange-600',
  strong_bearish: 'bg-red-100 text-red-700',
};


function RatingBadge({ rating }: { rating: string | null }): ReactElement | null {
  if (!rating) return null;
  const colors = RATING_BADGE_COLORS[rating] || 'bg-gray-100 text-gray-600';
  const label = rating.replace(/_/g, ' ');
  return (
    <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded ${colors}`}>
      {label}
    </span>
  );
}

function formatReturn(value: number | null): string {
  if (value == null) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

function returnColor(value: number | null): string {
  if (value == null) return 'text-gray-400';
  return value >= 0 ? 'text-emerald-600' : 'text-red-500';
}

// --- Metric Box (used in Signal Spotlight) ---
function MetricBox({ value, label }: { value: string; label: string; colorClass?: string }): ReactElement {
  return (
    <div className="bg-gray-50 rounded-md px-3 py-2 text-center min-w-[64px]">
      <div className="text-sm font-mono font-semibold text-gray-800">{value}</div>
      <div className="text-[10px] text-gray-400 uppercase mt-0.5">{label}</div>
    </div>
  );
}

// --- Signal Spotlight Lead ---
function SignalSpotlight({ article }: { article: TradingArticleSummary }): ReactElement {
  return (
    <div className="group relative bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-all duration-150">
      <a href={articleUrl(article)} className="block">
        {/* Row 1: Symbol + Study Type + Rating */}
        <div className="flex items-center gap-3 mb-3">
          {article.primary_symbol && (
            <span className="text-2xl font-mono font-bold text-gray-900">{article.primary_symbol}</span>
          )}
          <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
            {studyTypeLabel(article.study_type)}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <RatingBadge rating={article.signal_rating} />
          </div>
        </div>

        {/* Headline + Summary */}
        <h2 className="text-2xl font-bold text-gray-900 group-hover:text-gray-700 transition-colors leading-snug mb-2" style={{ fontFamily: 'Georgia, serif' }}>
          {article.title}
        </h2>
        {article.summary && (
          <p className="text-base text-gray-500 leading-relaxed mb-4">
            {article.summary}
          </p>
        )}

        {/* Metric Boxes */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {article.avg_forward_return_1m != null && (
            <MetricBox value={formatReturn(article.avg_forward_return_1m)} label="1M" />
          )}
          {article.avg_forward_return_3m != null && (
            <MetricBox value={formatReturn(article.avg_forward_return_3m)} label="3M" />
          )}
          {article.avg_forward_return_6m != null && (
            <MetricBox value={formatReturn(article.avg_forward_return_6m)} label="6M" />
          )}
          {article.win_rate_3m != null && (
            <MetricBox value={`${article.win_rate_3m.toFixed(0)}%`} label="Win" />
          )}
        </div>

        {/* Time ago */}
        <div className="text-right text-xs text-gray-400">{article.time_ago}</div>
      </a>
      <div className="absolute top-3 right-3">
        <BookmarkButton
          itemType="trading"
          itemId={article.id}
          title={article.title}
          subtitle={article.primary_symbol ?? undefined}
          url={articleUrl(article)}
          size="md"
        />
      </div>
    </div>
  );
}

// --- Signal Card (compact, for feed) ---
function SignalCard({ article }: { article: TradingArticleSummary }): ReactElement {
  return (
    <div className="group relative bg-white rounded-lg border border-gray-200 p-3 hover:shadow-sm hover:border-gray-300 transition-all duration-150">
      <a href={articleUrl(article)} className="block">
        {/* Row 1: Symbol + Study Type + Rating */}
        <div className="flex items-center gap-2 mb-1 pr-8">
          {article.primary_symbol && (
            <span className="text-base font-mono font-bold text-gray-900">{article.primary_symbol}</span>
          )}
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            {studyTypeLabel(article.study_type)}
          </span>
          <div className="ml-auto">
            <RatingBadge rating={article.signal_rating} />
          </div>
        </div>

        {/* Headline (single line) */}
        <h3 className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors leading-snug line-clamp-1 mb-1.5">
          {article.title}
        </h3>

        {/* Bottom row: key metrics */}
        <div className="flex items-center gap-2 text-[11px] font-mono text-gray-400">
          {article.avg_forward_return_3m != null && (
            <span className={returnColor(article.avg_forward_return_3m)}>
              {formatReturn(article.avg_forward_return_3m)} 3m
            </span>
          )}
          {article.avg_forward_return_3m != null && article.win_rate_3m != null && (
            <span>&middot;</span>
          )}
          {article.win_rate_3m != null && (
            <span className="text-slate-500">{article.win_rate_3m.toFixed(0)}% win</span>
          )}
          <span className="ml-auto text-gray-400">{article.time_ago}</span>
        </div>
      </a>
      <div className="absolute top-1.5 right-1.5">
        <BookmarkButton
          itemType="trading"
          itemId={article.id}
          title={article.title}
          subtitle={article.primary_symbol ?? undefined}
          url={articleUrl(article)}
          size="sm"
        />
      </div>
    </div>
  );
}

// --- Section Row (headline + trading meta) ---
function SectionRow({ article }: { article: TradingArticleSummary }): ReactElement {
  return (
    <div className="group flex items-start gap-2.5 py-2.5">
      <a href={articleUrl(article)} className="flex-1 min-w-0 block">
        <h3 className="text-[13px] font-medium text-gray-700 group-hover:text-gray-900 transition-colors leading-snug line-clamp-2">
          {article.title}
        </h3>
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-400">
          {article.primary_symbol && (
            <>
              <span className="font-mono font-semibold text-slate-600">{article.primary_symbol}</span>
              <span>&middot;</span>
            </>
          )}
          <span className="uppercase">{studyTypeLabel(article.study_type)}</span>
          <RatingBadge rating={article.signal_rating} />
          {article.avg_forward_return_3m != null && (
            <>
              <span>&middot;</span>
              <span className={`font-mono ${returnColor(article.avg_forward_return_3m)}`}>
                {formatReturn(article.avg_forward_return_3m)}
              </span>
            </>
          )}
          {article.win_rate_3m != null && (
            <>
              <span>&middot;</span>
              <span className="font-mono text-slate-500">{article.win_rate_3m.toFixed(0)}% win</span>
            </>
          )}
          <span>&middot;</span>
          <span>{article.time_ago}</span>
        </div>
      </a>
      <BookmarkButton
        itemType="trading"
        itemId={article.id}
        title={article.title}
        subtitle={article.primary_symbol ?? undefined}
        url={articleUrl(article)}
        size="sm"
      />
    </div>
  );
}

// --- Section Block ---
function SectionBlock({ sectionKey, data }: { sectionKey: string; data: { name: string; articles: TradingArticleSummary[]; total_count: number } }): ReactElement {
  if (data.articles.length === 0) return <></>;

  return (
    <div>
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
            {data.name}
          </h2>
          <span className="text-[10px] font-medium text-gray-400">
            {data.total_count}
          </span>
        </div>
        {data.total_count > data.articles.length && (
          <Link
            to={`/trading/section/${sectionKey}`}
            className="flex items-center gap-0.5 text-xs text-blue-600 hover:text-blue-500 transition-colors"
          >
            More
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
      <div className="divide-y divide-gray-50">
        {data.articles.map((article) => (
          <SectionRow key={article.id} article={article} />
        ))}
      </div>
    </div>
  );
}

// --- Main Portal ---
export default function TradingPortal(): ReactElement {
  const [searchInput, setSearchInput] = useState('');
  const [activeSymbol, setActiveSymbol] = useState<string | undefined>();
  const [searchPage, setSearchPage] = useState(0);
  const searchLimit = 20;

  const isSearchMode = !!activeSymbol;

  // Portal query (normal mode)
  const { data: portalData, isLoading: portalLoading, error: portalError } = useQuery<TradingPortalData>({
    queryKey: ['trading-portal'],
    queryFn: async () => {
      const response = await tradingAPI.getPortalData(15);
      return response.data;
    },
    staleTime: 0,
    enabled: !isSearchMode,
  });

  // Search query (symbol mode)
  const { data: searchData, isLoading: searchLoading, error: searchError } = useQuery<TradingSearchResponse>({
    queryKey: ['trading-search', activeSymbol, searchPage],
    queryFn: async () => {
      const response = await tradingAPI.searchBySymbol(activeSymbol!, {
        limit: searchLimit,
        offset: searchPage * searchLimit,
      });
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    enabled: isSearchMode,
  });

  const isLoading = isSearchMode ? searchLoading : portalLoading;
  const error = isSearchMode ? searchError : portalError;

  const handleSearch = () => {
    const val = searchInput.trim().toUpperCase();
    if (val) {
      setSearchPage(0);
      setActiveSymbol(val);
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setActiveSymbol(undefined);
    setSearchPage(0);
  };

  const SECTION_ORDER = ['advanced', 'breakouts', 'breakdowns', 'momentum', 'gaps', 'targets'];
  const sectionKeys = portalData
    ? SECTION_ORDER.filter((k) => portalData.sections[k]?.articles?.length)
    : [];

  const batchItems = (() => {
    if (isSearchMode && searchData) {
      return searchData.articles.map((a) => ({ itemType: 'trading' as const, itemId: a.id }));
    }
    if (!portalData) return [];
    return [
      ...(portalData.lead ? [{ itemType: 'trading' as const, itemId: portalData.lead.id }] : []),
      ...portalData.feed.map((a) => ({ itemType: 'trading' as const, itemId: a.id })),
      ...sectionKeys.flatMap((k) =>
        portalData.sections[k].articles.map((a) => ({ itemType: 'trading' as const, itemId: a.id }))
      ),
    ];
  })();

  return (
    <BookmarkBatchProvider items={batchItems}>
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 py-6">

        {/* Symbol Search */}
        <div className="mb-6">
          <form
            onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
            className="flex items-center gap-2 max-w-sm"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
                placeholder="Search by symbol..."
                className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 font-mono placeholder:font-sans"
              />
            </div>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors"
            >
              Search
            </button>
            {isSearchMode && (
              <button
                type="button"
                onClick={clearSearch}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </form>
          {isSearchMode && searchData && (
            <p className="text-xs text-gray-500 mt-2">
              {searchData.total_count} signal{searchData.total_count !== 1 ? 's' : ''} found for <span className="font-mono font-semibold text-gray-700">{activeSymbol}</span>
            </p>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" />
            <p className="text-sm text-gray-500">Loading signals...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <Activity className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Failed to load signals</h3>
            <p className="text-sm text-gray-500">Please try again later</p>
          </div>
        )}

        {/* Search Results */}
        {isSearchMode && searchData && !isLoading && (
          <>
            {searchData.articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {searchData.articles.map((article) => (
                  <SignalCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No signals found</h3>
                <p className="text-sm text-gray-500">No trading signals match <span className="font-mono font-semibold">{activeSymbol}</span></p>
              </div>
            )}

            {/* Pagination */}
            <Pagination
              page={searchPage}
              totalPages={searchData.total_pages}
              onPageChange={setSearchPage}
              hasMore={searchData.has_more}
            />
          </>
        )}

        {/* Portal Content (normal mode) */}
        {!isSearchMode && portalData && !isLoading && (
          <>
            {/* Signal Spotlight — full width */}
            {portalData.lead && (
              <div className="mb-6">
                <SignalSpotlight article={portalData.lead} />
              </div>
            )}

            {/* Feed: 2-column grid of signal cards (even count) */}
            {portalData.feed.length > 0 && (() => {
              const items = portalData.feed.slice(0, portalData.feed.length - (portalData.feed.length % 2));
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-8">
                  {items.map((article) => (
                    <SignalCard key={article.id} article={article} />
                  ))}
                </div>
              );
            })()}

            {/* Section Blocks (2x3 grid) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-6 border-t border-gray-200">
              {sectionKeys.map((sectionKey) => {
                const sectionData = portalData.sections[sectionKey];
                if (!sectionData || sectionData.articles.length === 0) return null;
                return (
                  <SectionBlock
                    key={sectionKey}
                    sectionKey={sectionKey}
                    data={sectionData}
                  />
                );
              })}
            </div>

            {/* Empty State */}
            {portalData.total_articles === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                  <Activity className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No signals yet</h3>
                <p className="text-sm text-gray-500">Check back later</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </BookmarkBatchProvider>
  );
}
